import React, { useEffect, useState } from 'react';
import Filters from '../../../components/Filter';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import CameraTypeDropdown from '../../../components/Filters/Cameras/CameraTypeDropdown';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import CameraTagsDropdown from '../../../components/Filters/Cameras/CameraTagsDropdown';
import { View } from 'react-native';
import PolygonTypeDropdown from '../../../components/PolygonTypeDropdown';
import PolygonByTypeDropdown from '../../../components/PolygonByTypeDropdown';
import { IntegrationType } from '../../../schemas/cameras';
import CamerasThirdPartyOriginDropdown from '../../../components/cameras/CamerasThirdPartyOriginDropdown';
import IntegrationTypesSelector from '../../../components/Filters/Cameras/IntegrationTypesSelector';
// import CamerasThirdPartyImageProviderDropdown from '../../../components/cameras/CamerasThirdPartyImageProviderDropdown';
// import CamerasThirdPartyCloudProviderDropdown from '../../../components/cameras/CamerasThirdPartyCloudProviderDropdown';

interface Props {
    filters: CamerasParamList['CamerasList'];
    setFilters: React.Dispatch<React.SetStateAction<CamerasParamList['CamerasList']>>;
    textFilter: string;
    setTextFilter: React.Dispatch<React.SetStateAction<string>>;
}

function Filter({ filters, setFilters, textFilter, setTextFilter }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<CamerasParamList, 'CamerasList'>>>();

    const [onlineFilter, setOnlineFilter] = useState(filters.online ?? false);
    const [offlineFilter, setOfflineFilter] = useState(filters.offline ?? false);
    const [selectedCameraTypeFilter, setSelectedCameraTypeFilter] = useState<CameraType | 'all'>(filters.type ?? 'all');
    const [selectedTagsFilter, setSelectedTagsFilter] = useState<number[]>(filters.tagsIds ?? []);
    const [selectedIntegrationTypeFilter, setSelectedIntegrationTypeFilter] = useState<IntegrationType[]>(filters.integrationType ?? []);
    // TODO: remove when migrated to thirdPartyApprovedCompanies
    const [selectedThirdPartyOrigin, setSelectedThirdPartyOrigin] = useState<string>(filters.origin ?? '');
    const [selectedPolygonType, setSelectedPolygonType] = useState<string>(filters.polygonType ?? '');
    const [selectedPolygon, setSelectedPolygon] = useState<number>(filters.polygon ?? 0);
    const [polygonPendingLoading, setPolygonPendingLoading] = useState<boolean>(false);
    // TODO: Add when migrated to thirdPartyApprovedCompanies
    // const [selectedThirdPartyImageProvider, setSelectedThirdPartyImageProvider] = useState<string>(filters.imageProvider ?? '');
    // const [selectedThirdPartyCloudProvider, setSelectedThirdPartyCloudProvider] = useState<string>(filters.cloudProvider ?? '');

    const handleCleanFilter = () => {
        setTextFilter('');
        setOnlineFilter(true);
        setOfflineFilter(false);
        setSelectedTagsFilter([]);
        setSelectedCameraTypeFilter('all');
        setSelectedPolygonType('');
        setSelectedPolygon(0);
        // setSelectedThirdPartyImageProvider('');
        // setSelectedThirdPartyCloudProvider('');
        setSelectedIntegrationTypeFilter([]);
        setSelectedThirdPartyOrigin('');
    };

    useEffect(() => {
        setOnlineFilter(filters.online ?? false);
        setOfflineFilter(filters.offline ?? false);
    }, [filters]);

    const handleFilter = () => {
        setFilters({
            ...filters,
            page: 0,
            textFilter: textFilter ? textFilter : undefined,
            online: onlineFilter,
            offline: offlineFilter ? offlineFilter : undefined,
            tagsIds: selectedTagsFilter.length ? selectedTagsFilter : undefined,
            type: selectedCameraTypeFilter !== 'all' ? selectedCameraTypeFilter : undefined,
            integrationType: selectedIntegrationTypeFilter.length ? selectedIntegrationTypeFilter : undefined,
            origin: selectedThirdPartyOrigin.trim() !== '' ? selectedThirdPartyOrigin : undefined,
            polygonType: selectedPolygonType !== '' ? selectedPolygonType : undefined,
            polygon: selectedPolygon != 0 ? selectedPolygon : undefined,
            // imageProvider: selectedThirdPartyImageProvider != '' ? selectedThirdPartyImageProvider : undefined,
            // cloudProvider: selectedThirdPartyCloudProvider != '' ? selectedThirdPartyCloudProvider : undefined,
        });

        navigation.setParams({
            page: 1,
            textFilter: textFilter ? textFilter : undefined,
            online: onlineFilter ? onlineFilter : undefined,
            offline: offlineFilter ? offlineFilter : undefined,
            tagsIds: selectedTagsFilter.length ? selectedTagsFilter : undefined,
            type: selectedCameraTypeFilter !== 'all' ? selectedCameraTypeFilter : undefined,
            integrationType: selectedIntegrationTypeFilter.length ? selectedIntegrationTypeFilter : undefined,
            origin: selectedThirdPartyOrigin.trim() !== '' ? selectedThirdPartyOrigin : undefined,
            polygonType: selectedPolygonType !== '' ? selectedPolygonType : undefined,
            polygon: selectedPolygon != 0 ? selectedPolygon : undefined,
            // imageProvider: selectedThirdPartyImageProvider != '' ? selectedThirdPartyImageProvider : undefined,
            // cloudProvider: selectedThirdPartyCloudProvider != '' ? selectedThirdPartyCloudProvider : undefined,
        });
    };

    return (
        <Filters
            onCleanFilters={handleCleanFilter}
            onFilters={handleFilter}
            applyIsLoading={polygonPendingLoading}
            applyDisabled={selectedPolygonType != '' && selectedPolygon == 0}
        >
            <FilterInput
                label={translate('typeToSearch')}
                value={textFilter}
                placeholder={translate('typeToSearch')}
                onChange={setTextFilter}
            />
            <View style={{ flexDirection: 'row' }}>
                <FilterCheckBox
                    label={translate('online')}
                    value={onlineFilter}
                    onChange={(value) => {
                        if (!value && !offlineFilter) {
                            return;
                        }
                        setOnlineFilter(value);
                    }}
                />
                <FilterCheckBox
                    label={translate('offline')}
                    value={offlineFilter}
                    onChange={(value) => {
                        if (!value && !onlineFilter) {
                            return;
                        }
                        setOfflineFilter(value);
                    }}
                />
            </View>
            <CameraTypeDropdown
                value={selectedCameraTypeFilter}
                setValue={setSelectedCameraTypeFilter}
                includeAll={true}
                zIndex={7}
            />
            <CameraTagsDropdown value={selectedTagsFilter} setValue={setSelectedTagsFilter} zIndex={6} />
            <IntegrationTypesSelector
                value={selectedIntegrationTypeFilter}
                setValue={setSelectedIntegrationTypeFilter}
                showType1={true}
            />
            <CamerasThirdPartyOriginDropdown
                value={selectedThirdPartyOrigin}
                setValue={setSelectedThirdPartyOrigin}
                integrationType={'all'}
                zIndex={4}
            />
            {/* <CamerasThirdPartyImageProviderDropdown
                value={selectedThirdPartyImageProvider}
                setValue={setSelectedThirdPartyImageProvider}
                zIndex={5}
            />
            <CamerasThirdPartyCloudProviderDropdown
                value={selectedThirdPartyCloudProvider}
                setValue={setSelectedThirdPartyCloudProvider}
                zIndex={4}
            /> */}
            <PolygonTypeDropdown
                label={translate('regions')}
                dropDownDirection='BOTTOM'
                showTooltip
                tooltipText={translate('regionsInfo')}
                zIndex={3}
                setPolygonType={setSelectedPolygonType}
                polygonType={selectedPolygonType}
            />
            {selectedPolygonType != '' &&
                <PolygonByTypeDropdown
                    label={translate('subRegions')}
                    dropDownDirection='BOTTOM'
                    setPolygon={setSelectedPolygon}
                    polygon={selectedPolygon}
                    polygonType={selectedPolygonType}
                    showTooltip
                    tooltipText={translate('subRegionsInfo')}
                    zIndex={2}
                    setPendingLoading={setPolygonPendingLoading}
                />
            }
        </Filters>
    );
}


export default Filter;
